import type { KaraokeEnvelope } from "./karaoke";

function audioContext(): AudioContext {
  const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) throw new Error("This browser cannot decode audio for karaoke.");
  return new Ctor();
}

async function decode(blob: Blob): Promise<AudioBuffer> {
  const ctx = audioContext();
  try {
    const copy = blob.slice(0);
    const buf = await copy.arrayBuffer();
    return await ctx.decodeAudioData(buf.slice(0));
  } finally {
    void ctx.close?.();
  }
}

function mixMono(buffer: AudioBuffer): Float32Array {
  const n = buffer.length;
  const out = new Float32Array(n);
  const chs = buffer.numberOfChannels;
  for (let c = 0; c < chs; c += 1) {
    const d = buffer.getChannelData(c);
    for (let i = 0; i < n; i += 1) out[i] += d[i];
  }
  if (chs > 1) {
    const inv = 1 / chs;
    for (let i = 0; i < n; i += 1) out[i] *= inv;
  }
  return out;
}

export async function karaokeEnvelopeFromBlob(blob: Blob): Promise<{ duration: number; env: KaraokeEnvelope }> {
  const buffer = await decode(blob);
  const sr = buffer.sampleRate || 44100;
  const hop = Math.max(512, Math.round(sr * 0.05));
  const hopSec = hop / sr;
  const mono = mixMono(buffer);
  const rms = new Float32Array(Math.max(1, Math.ceil(mono.length / hop)));
  for (let i = 0, b = 0; i < mono.length; i += hop, b += 1) {
    const end = Math.min(mono.length, i + hop);
    let acc = 0;
    for (let k = i; k < end; k += 1) acc += mono[k] * mono[k];
    rms[b] = Math.sqrt(acc / Math.max(1, end - i));
  }
  return { duration: buffer.duration, env: { hop: hopSec, rms } };
}

export async function karaokeEnvelopeFromUrl(url: string): Promise<{ duration: number; env: KaraokeEnvelope } | null> {
  if (!url) return null;
  const res = await fetch(url);
  if (!res.ok) return null;
  const blob = await res.blob();
  if (!blob.size) return null;
  return karaokeEnvelopeFromBlob(blob);
}

function onePole(x: number, prev: number, a: number): number {
  return prev + a * (x - prev);
}

function coeff(sr: number, hz: number): number {
  const rc = 1 / (2 * Math.PI * Math.max(1, hz));
  const dt = 1 / sr;
  return dt / (rc + dt);
}

/** Mid-side vocal duck. Stereo: cut centered mid in the vocal band. Mono: notch that band. */
export function vocalReduceBuffer(buffer: AudioBuffer): AudioBuffer {
  const sr = buffer.sampleRate;
  const n = buffer.length;
  const stereo = buffer.numberOfChannels > 1;
  const L = buffer.getChannelData(0);
  const R = stereo ? buffer.getChannelData(1) : L;
  const outL = new Float32Array(n);
  const outR = new Float32Array(n);
  const lpA = coeff(sr, 180);
  const hpA = coeff(sr, 5200);
  let lp = 0;
  let hp = 0;
  const duck = stereo ? 0.1 : 0.22;
  for (let i = 0; i < n; i += 1) {
    const left = L[i] || 0;
    const right = R[i] || 0;
    const mid = stereo ? 0.5 * (left + right) : left;
    const side = stereo ? 0.5 * (left - right) : 0;
    lp = onePole(mid, lp, lpA);
    hp = onePole(mid, hp, hpA);
    const air = mid - hp;
    const vocal = mid - lp - air;
    const midOut = lp + air + vocal * duck;
    const wide = side * (stereo ? 1.18 : 0);
    let oL = midOut + wide;
    let oR = midOut - wide;
    const peak = Math.max(Math.abs(oL), Math.abs(oR));
    if (peak > 0.98) {
      const g = 0.98 / peak;
      oL *= g;
      oR *= g;
    }
    outL[i] = oL;
    outR[i] = oR;
  }
  const off = new OfflineAudioContext(2, n, sr);
  const out = off.createBuffer(2, n, sr);
  out.copyToChannel(outL, 0);
  out.copyToChannel(outR, 1);
  return out;
}

function floatTo16(x: number): number {
  const s = Math.max(-1, Math.min(1, x));
  return s < 0 ? Math.round(s * 0x8000) : Math.round(s * 0x7fff);
}

export function encodeWav(buffer: AudioBuffer): Blob {
  const chs = Math.min(2, Math.max(1, buffer.numberOfChannels));
  const sr = buffer.sampleRate;
  const n = buffer.length;
  const bytesPer = 2;
  const block = chs * bytesPer;
  const dataSize = n * block;
  const out = new ArrayBuffer(44 + dataSize);
  const view = new DataView(out);
  const ascii = (offset: number, s: string) => {
    for (let i = 0; i < s.length; i += 1) view.setUint8(offset + i, s.charCodeAt(i));
  };
  ascii(0, "RIFF");
  view.setUint32(4, 36 + dataSize, true);
  ascii(8, "WAVE");
  ascii(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, chs, true);
  view.setUint32(24, sr, true);
  view.setUint32(28, sr * block, true);
  view.setUint16(32, block, true);
  view.setUint16(34, 16, true);
  ascii(36, "data");
  view.setUint32(40, dataSize, true);
  const ch = Array.from({ length: chs }, (_, i) => buffer.getChannelData(Math.min(i, buffer.numberOfChannels - 1)));
  let p = 44;
  for (let i = 0; i < n; i += 1) {
    for (let c = 0; c < chs; c += 1) {
      view.setInt16(p, floatTo16(ch[c][i] || 0), true);
      p += 2;
    }
  }
  return new Blob([out], { type: "audio/wav" });
}

export async function makeKaraokeBed(blob: Blob): Promise<Blob> {
  const decoded = await decode(blob);
  const reduced = vocalReduceBuffer(decoded);
  return encodeWav(reduced);
}
